import React, { useState, useEffect, useRef } from 'react';
import {
  User,
  Upload,
  Trash2,
  Save,
  Loader2,
  AlertCircle,
  Check,
  Calendar,
  Clock,
  Hash,
  Palette,
  Languages,
  Briefcase,
  Award,
  Building
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserPreferences } from '../../../../contexts/UserPreferencesContext';
import { useTheme } from '../../../../contexts/ThemeContext';
import { settingsService } from '../../../../services/settingsService';

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' }
];

const TIME_FORMATS = [
  { value: '12h', label: '12-hour (2:30 PM)' },
  { value: '24h', label: '24-hour (14:30)' }
];

const FIRST_DAY_OPTIONS = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' }
];

const NUMBER_FORMATS = [
  { value: 'en-AU', label: 'Australian (1,234.56)' },
  { value: 'en-US', label: 'US (1,234.56)' },
  { value: 'de-DE', label: 'German (1.234,56)' },
  { value: 'fr-FR', label: 'French (1 234,56)' }
];

const ProfileTab = () => {
  const { user, refreshProfile } = useAuth();
  const { updatePreferences } = useUserPreferences();
  const { themePreference, setThemePreference } = useTheme();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Profile state
  const [profile, setProfile] = useState({
    display_name: '',
    phone: '',
    bio: '',
    avatar_url: null
  });

  // Professional info state (contractor-specific)
  const [professionalInfo, setProfessionalInfo] = useState({
    trade: '',
    years_experience: '',
    certifications: '',
    abn: ''
  });

  // Display preferences state
  const [displayPrefs, setDisplayPrefs] = useState({
    date_format: 'DD/MM/YYYY',
    time_format: '12h',
    first_day_of_week: 'monday',
    number_format: 'en-AU',
    theme: 'dark',
    language: 'en'
  });

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      try {
        const { data } = await settingsService.getUserProfile();
        setProfile({
          display_name: data?.display_name || data?.full_name || '',
          phone: data?.phone || '',
          bio: data?.bio || '',
          avatar_url: data?.avatar_url
        });
        setProfessionalInfo({
          trade: data?.trade || '',
          years_experience: data?.years_experience || '',
          certifications: data?.certifications || '',
          abn: data?.abn || ''
        });
        setDisplayPrefs({
          date_format: data?.date_format || 'DD/MM/YYYY',
          time_format: data?.time_format || '12h',
          first_day_of_week: data?.first_day_of_week || 'monday',
          number_format: data?.number_format || 'en-AU',
          theme: data?.theme || 'dark',
          language: data?.language || 'en'
        });
      } catch (e) {
        console.warn('Extended profile columns may not exist:', e.message);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 512000) {
      setError('Avatar must be less than 500KB');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Avatar must be PNG, JPG, or WebP');
      return;
    }

    try {
      setUploadingAvatar(true);
      setError(null);
      const { url } = await settingsService.uploadAvatar(file);
      setProfile(prev => ({ ...prev, avatar_url: url }));
      await refreshProfile?.();
      setSuccess('Avatar uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setError('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      setUploadingAvatar(true);
      await settingsService.deleteAvatar();
      setProfile(prev => ({ ...prev, avatar_url: null }));
      await refreshProfile?.();
      setSuccess('Avatar removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting avatar:', err);
      setError('Failed to remove avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);

      await settingsService.updateUserProfile({
        display_name: profile.display_name,
        phone: profile.phone,
        bio: profile.bio,
        // Professional info
        trade: professionalInfo.trade,
        years_experience: professionalInfo.years_experience ? parseInt(professionalInfo.years_experience) : null,
        certifications: professionalInfo.certifications,
        abn: professionalInfo.abn
      });

      setSuccess('Profile saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDisplayPrefs = async () => {
    try {
      setSaving(true);
      setError(null);

      await settingsService.updateDisplayPreferences(displayPrefs);

      // Update the global preferences context so changes reflect immediately
      updatePreferences({
        date_format: displayPrefs.date_format,
        time_format: displayPrefs.time_format,
        first_day_of_week: displayPrefs.first_day_of_week,
        number_format: displayPrefs.number_format
      });

      setSuccess('Preferences saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Profile</h2>
        <p className="text-text-secondary text-sm mt-1">
          Manage your personal and professional information
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle size={16} className="text-red-500" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <Check size={16} className="text-green-500" />
          <span className="text-green-400 text-sm">{success}</span>
        </div>
      )}

      {/* ==================== AVATAR & PERSONAL INFO ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <User size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Personal Information</h3>
        </div>

        {/* Avatar */}
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-surface rounded-full flex items-center justify-center overflow-hidden border border-border shrink-0">
            {uploadingAvatar ? (
              <Loader2 size={24} className="text-orange-500 animate-spin" />
            ) : profile.avatar_url ? (
              <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold text-text-secondary">
                {profile.display_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-surface text-text-primary rounded-lg hover:bg-surface-light transition-colors text-sm">
                <Upload size={16} className="mr-2" />
                Upload Photo
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  disabled={uploadingAvatar}
                />
              </label>
              {profile.avatar_url && (
                <button
                  onClick={handleDeleteAvatar}
                  disabled={uploadingAvatar}
                  className="p-2 text-text-secondary hover:text-red-400 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
            <p className="text-xs text-text-secondary">PNG, JPG, WebP up to 500KB.</p>
          </div>
        </div>

        {/* Personal Info Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">Display Name</label>
            <input
              type="text"
              value={profile.display_name}
              onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-secondary cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Phone Number</label>
            <input
              type="tel"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
              placeholder="+61 400 000 000"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-text-secondary text-sm mb-2">Bio / About Me</label>
            <textarea
              value={profile.bio}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value.slice(0, 200) })}
              rows={2}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary resize-none"
              placeholder="A brief description about yourself..."
            />
            <p className="text-xs text-text-secondary mt-1">{profile.bio.length}/200 characters</p>
          </div>
        </div>
      </div>

      {/* ==================== PROFESSIONAL INFORMATION ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Briefcase size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Professional Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Briefcase size={14} className="inline mr-1" /> Trade / Job Title
            </label>
            <input
              type="text"
              value={professionalInfo.trade}
              onChange={(e) => setProfessionalInfo({ ...professionalInfo, trade: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
              placeholder="e.g., Electrician, Cleaner, Security Guard"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Years of Experience</label>
            <input
              type="number"
              min="0"
              max="50"
              value={professionalInfo.years_experience}
              onChange={(e) => setProfessionalInfo({ ...professionalInfo, years_experience: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
              placeholder="0"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-text-secondary text-sm mb-2">
              <Award size={14} className="inline mr-1" /> Certifications & Licenses
            </label>
            <textarea
              value={professionalInfo.certifications}
              onChange={(e) => setProfessionalInfo({ ...professionalInfo, certifications: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary resize-none"
              placeholder="e.g., White Card, First Aid, Security License"
            />
            <p className="text-xs text-text-secondary mt-1">List your relevant certifications and licenses</p>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Building size={14} className="inline mr-1" /> ABN (Optional)
            </label>
            <input
              type="text"
              value={professionalInfo.abn}
              onChange={(e) => setProfessionalInfo({ ...professionalInfo, abn: e.target.value.replace(/\D/g, '').slice(0, 11) })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
              placeholder="XX XXX XXX XXX"
            />
            <p className="text-xs text-text-secondary mt-1">Australian Business Number</p>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center touch-target-sm"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
            Save Profile
          </button>
        </div>
      </div>

      {/* ==================== DISPLAY PREFERENCES ==================== */}
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <Palette size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Display Preferences</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Calendar size={14} className="inline mr-1" /> Date Format
            </label>
            <select
              value={displayPrefs.date_format}
              onChange={(e) => setDisplayPrefs({ ...displayPrefs, date_format: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              {DATE_FORMATS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Clock size={14} className="inline mr-1" /> Time Format
            </label>
            <select
              value={displayPrefs.time_format}
              onChange={(e) => setDisplayPrefs({ ...displayPrefs, time_format: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              {TIME_FORMATS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Calendar size={14} className="inline mr-1" /> First Day of Week
            </label>
            <select
              value={displayPrefs.first_day_of_week}
              onChange={(e) => setDisplayPrefs({ ...displayPrefs, first_day_of_week: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              {FIRST_DAY_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Hash size={14} className="inline mr-1" /> Number Format
            </label>
            <select
              value={displayPrefs.number_format}
              onChange={(e) => setDisplayPrefs({ ...displayPrefs, number_format: e.target.value })}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              {NUMBER_FORMATS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Palette size={14} className="inline mr-1" /> Theme
            </label>
            <select
              value={themePreference}
              onChange={(e) => setThemePreference(e.target.value)}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            >
              <option value="dark">Dark</option>
              <option value="light">Light</option>
              <option value="system">System</option>
            </select>
            <p className="text-xs text-text-tertiary mt-1">
              {themePreference === 'system' ? 'Follows your system preference' : `Using ${themePreference} theme`}
            </p>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Languages size={14} className="inline mr-1" /> Language
            </label>
            <select
              value={displayPrefs.language}
              disabled
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-secondary cursor-not-allowed"
            >
              <option value="en">English</option>
            </select>
            <p className="text-xs text-text-secondary mt-1">Additional languages coming soon</p>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={handleSaveDisplayPrefs}
            disabled={saving}
            className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center touch-target-sm"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;
